import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as session from '../../lib/session'
import { AccountContext, type AuthStatus } from '../../hooks/account-context'

/* The buyer's session, lifted into React.

   The token itself lives in lib/session, outside the tree, because axios
   needs it on every request and axios is not a component. This is the part
   that re-renders when it changes — including when it changes without
   anybody clicking anything, which is what the subscription below is for: a
   401 on a background refetch clears the session in that module, and the
   page has to notice and show the sign-in form rather than a list of
   errors. */

export function AccountProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient()

  const [current, setCurrent] = useState(() => session.getSession())
  const [status, setStatus] = useState<AuthStatus>(() =>
    session.getSession() ? 'checking' : 'signed-out',
  )

  useEffect(() => {
    const unsubscribe = session.onSessionChange((next) => {
      setCurrent(next)
      setStatus(next ? 'signed-in' : 'signed-out')

      /* Every account row was fetched as somebody. Dropping those on any
         change of session is what stops the next person to sign in on a
         shared machine from seeing the last one's bookings for a moment
         while the refetch is in flight. The catalogue is public and is left
         alone: it is the same list for everyone. */
      if (!next) client.removeQueries({ queryKey: ['account'] })
    })

    return () => {
      unsubscribe()
    }
  }, [client])

  /* A stored token is believed enough to render the page, but checked
     against the API straight away — see lib/session.verifySession for why a
     token that has not expired can still be refused. */
  useEffect(() => {
    if (!session.getSession()) return

    let cancelled = false

    void session.verifySession().then((verified) => {
      if (cancelled) return
      setCurrent(verified)
      setStatus(verified ? 'signed-in' : 'signed-out')
    })

    return () => {
      cancelled = true
    }
  }, [])

  /* No setState in either of these: both write through lib/session, which
     notifies the subscription above. One path in, so signing in and being
     signed in by a restored token cannot drift apart. */
  const signIn = useCallback(async (username: string, password: string) => {
    await session.signIn(username, password)
  }, [])

  const register = useCallback(async (input: session.RegisterInput) => {
    await session.register(input)
  }, [])

  const signOut = useCallback(() => {
    session.signOut()
  }, [])

  const value = useMemo(() => {
    const permissions = current?.user.permissions ?? []

    return {
      status,
      session: current,
      can: (permission: string) => permissions.includes(permission),
      signIn,
      register,
      signOut,
    }
  }, [current, status, signIn, register, signOut])

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}
